// ✅ ViewBookingPage.jsx
import "./viewBooking.css";
import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, MapPin, Navigation, RefreshCw, Star, User, X } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/es";
import usersRequest from "../../request/usersRequest";
import generalRequest from "../../request/generalRequest";
import BookingModal from "../bookingModal/bookingModal";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import ViewBookingSkeleton from "./viewBookingSkeleton/viewBookingSkeleton";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";
import BannerMessage from "../common/bannerMessage/bannerMessage";
import useModalDismiss from "../common/useModalDismiss";

/* This page reads times in the shop's zone and prints a relative countdown, so
   it registers its own dayjs plugins rather than relying on whichever route
   happened to load first. */
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
/* "LL" is the locale's own long date — "August 21, 2026" in English but
   "21 de agosto de 2026" in Spanish, which "MMMM D, YYYY" gets wrong. */
dayjs.extend(localizedFormat);

const CURRENCY_SYMBOLS = { USD: "$", DOP: "RD$", EUR: "€", CAD: "C$", MXN: "MX$" };

const formatPrice = (price, currency) => {
	const code = currency || "USD";
	const amount = Number(price ?? 0).toLocaleString("en-US");
	return CURRENCY_SYMBOLS[code] ? `${CURRENCY_SYMBOLS[code]}${amount}` : `${code} ${amount}`;
};

/* Statuses where the appointment is still ahead of the customer — the only
   ones where cancelling or rescheduling means anything. */
const ACTIVE_STATUSES = ["confirmed", "pending", "in_progress", "rescheduled"];

const DEFAULT_TZ = "America/Santo_Domingo";

const ViewBookingPage = () => {
	const [booking, setBooking] = useState({});
	const [review, setReview] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [shopData, setShopData] = useState(null);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [rescheduleData, setRescheduleData] = useState(null);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

	useModalDismiss(() => setConfirmCancelOpen(false), { enabled: confirmCancelOpen });

	const { bookingId } = useParams();

	const navigate = useNavigate();
	const location = useLocation();

	const { language, userData } = useContext(CombinedContext);

	const isLoggedIn = !!(userData && userData.cognitoTokens?.IdToken);

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		if (location.state?.reviewSubmitted) {
			showBanner(
				"success",
				location.state.isEdit ? translations[language].viewBooking.misc.reviewUpdated : translations[language].viewBooking.misc.reviewSubmitted,
			);
		}
	}, []);

	useEffect(() => {
		/* The router reuses this component across /booking/:bookingId changes,
		   so clear the previous booking before loading the next one — otherwise
		   its details stay on screen while the new one loads, and stick around
		   entirely if the new fetch fails. */
		setLoading(true);
		setError(null);
		setBooking({});
		setReview(null);

		async function loadBooking() {
			try {
				const data = await usersRequest.getBooking(bookingId);
				setBooking(data.booking || {});
				setReview(data.review || null);
			} catch (err) {
				/* Keep the key rather than the sentence: resolving it here froze the
				   message in whichever language was active when the fetch failed, so
				   switching language afterwards left the error behind in the old one. */
				setError("load");
				console.error(err);
			} finally {
				setLoading(false);
			}
		}
		loadBooking();
	}, [bookingId]);

	if (loading) return <ViewBookingSkeleton />;

	const t = translations[language].viewBooking;

	/* `navigate(-1)` has nowhere to go when this page is the first entry in the
	   tab's history — a link out of a confirmation email, a QR code, "open in
	   new tab" — and the back button just sits there doing nothing. The router
	   keeps its position in `history.state.idx`, so fall back to a real route
	   whenever there is no earlier entry to return to. */
	const goBack = () => {
		const idx = window.history.state?.idx;
		const canGoBack = typeof idx === "number" ? idx > 0 : location.key !== "default";

		if (canGoBack) {
			navigate(-1);
			return;
		}

		if (isLoggedIn) navigate("/account/bookings", { replace: true });
		else navigate(booking.shopId ? `/shops/${booking.shopId}` : "/", { replace: true });
	};

	if (error) {
		return (
			<div className="sf-vb-page">
				<div className="sf-vb-content sf-vb-content-error">
					<div className="sf-vb-error">
						<p>{t.errors[error] || t.errors.load}</p>
						<button onClick={goBack}>{t.manage.backBtn}</button>
					</div>
				</div>
			</div>
		);
	}

	const addressQuery = encodeURIComponent(`${booking.address || ""}, ${booking.city || ""}, ${booking.country || ""}`);

	async function handleReschedule() {
		try {
			const res = await generalRequest.getShopWithstaffsByIdPublic(booking.shopId);
			setShopData(res.shop);
			setRescheduleData(booking);
			setIsBookingOpen(true);
		} catch (err) {
			console.error("Reschedule failed:", err);
			showBanner("error", t.errors.reschedule);
		}
	}

	async function reloadBooking() {
		try {
			const data = await usersRequest.getBooking(bookingId);
			setBooking(data.booking || {});
			setReview(data.review || null);
		} catch (err) {
			console.error("Failed to reload booking", err);
			showBanner("error", t.errors.reload);
		}
	}

	async function confirmCancelBooking() {
		try {
			await usersRequest.cancelBookingCustomer(booking.bookingId);

			setBooking({ ...booking, status: "cancelled" });
			setConfirmCancelOpen(false);

			setTimeout(() => {
				if (isLoggedIn) {
					/* the bookings list lives under /account on web — "/bookings"
					   is not a route here and sent the user nowhere */
					navigate("/account/bookings");
				} else {
					navigate(`/shops/${booking.shopId}` || "/");
				}
			}, 800);
		} catch (err) {
			console.error("Cancel failed:", err);
			showBanner("error", t.errors.cancel);
		}
	}

	const renderStars = (rating, size = 16) =>
		[1, 2, 3, 4, 5].map((i) => (
			<Star key={i} size={size} strokeWidth={1.5} fill="currentColor" className={i <= rating ? undefined : "is-off"} />
		));

	/* ── Derived view data ───────────────────────────────────────── */
	const zone = booking.timeZone || DEFAULT_TZ;
	const start = booking.startTime ? dayjs.tz(booking.startTime, zone).locale(language) : null;
	const end = booking.endTime ? dayjs.tz(booking.endTime, zone).locale(language) : null;
	const day = start || (booking.date ? dayjs(booking.date).locale(language) : null);

	const isActive = ACTIVE_STATUSES.includes(booking.status);
	const countdown = isActive && booking.startTime && dayjs(booking.startTime).isAfter(dayjs()) ? dayjs(booking.startTime).locale(language).fromNow() : null;

	const services = Array.isArray(booking.services) ? booking.services : [];
	const serviceName = (s) => (language === "es" ? s.serviceNameEs : s.serviceNameEn) || s.name || s.serviceNameEn || s.serviceNameEs;
	const totalPrice = services.reduce((sum, s) => sum + (Number(s.price) || 0), 0);
	const staffInitial = booking.staffName?.trim()?.charAt(0)?.toUpperCase();

	const hasPlace = booking.address || (booking.lat && booking.lng);

	return (
		<div className="sf-vb-page">
			<div className="sf-vb-content">
				{/* ═══ HEAD — spans both columns so the ticket and the rail
				     start level however long the shop name runs ═══ */}
				<header className="sf-vb-head">
					<button className="sf-vb-back" onClick={goBack} aria-label={t.manage.backBtn}>
						<ArrowLeft size={18} strokeWidth={2} />
						<span>{t.manage.backBtn}</span>
					</button>

					<div className="sf-vb-head-copy">
						{/* <span className="sf-vb-eyebrow">{t.header.eyebrow}</span> */}
						<h1 className="sf-vb-shop">{booking.shopName}</h1>

						<div className="sf-vb-meta">
							<span className={`sf-vb-status is-${booking.status || "pending"}`}>
								<i className="sf-vb-status-dot" />
								{t.status[booking.status] || booking.status}
							</span>
							{countdown && <span className="sf-vb-countdown">{countdown}</span>}
						</div>
					</div>
				</header>

				{/* ═══ MAIN ═══ */}
				<div className="sf-vb-main">
					{/* ── TICKET ── */}
					<section className="sf-vb-ticket">
						<div className="sf-vb-ticket-top">
							{day && (
								<div className="sf-vb-date">
									<em>{day.format("MMM")}</em>
									<strong>{day.format("D")}</strong>
									<span>{day.format("ddd")}</span>
								</div>
							)}

							<div className="sf-vb-when">
								<p className="sf-vb-label">{t.details.time}</p>
								<div className="sf-vb-time">
									{start ? start.format("h:mm A") : "—"}
									{end && <span className="sf-vb-time-end"> – {end.format("h:mm A")}</span>}
								</div>
								{day && <div className="sf-vb-longdate">{day.format("dddd, LL")}</div>}
							</div>

							<div className="sf-vb-who">
								<span className={`sf-vb-avatar ${staffInitial ? "" : "is-empty"}`}>{staffInitial || <User size={18} strokeWidth={1.8} />}</span>
								<span className="sf-vb-who-copy">
									<em>{t.details.staff}</em>
									<strong>{booking.staffName || "—"}</strong>
								</span>
							</div>
						</div>

						<div className="sf-vb-perf" aria-hidden="true" />

						<div className="sf-vb-ticket-body">
							{services.length > 0 ? (
								<>
									<p className="sf-vb-label">{t.details.services}</p>
									<ul className="sf-vb-services">
										{services.map((s, i) => (
											<li key={s.serviceId || i}>
												<span className="sf-vb-service-name">{serviceName(s)}</span>
												{s.duration ? <span className="sf-vb-service-dur">{t.details.duration.replace("{{count}}", s.duration)}</span> : null}
												{Number(s.price) > 0 ? <span className="sf-vb-service-price">{formatPrice(s.price, booking.currency)}</span> : null}
											</li>
										))}
									</ul>

									{totalPrice > 0 && (
										<div className="sf-vb-total">
											<span>{t.details.total}</span>
											<strong>{formatPrice(totalPrice, booking.currency)}</strong>
										</div>
									)}
								</>
							) : (
								<p className="sf-vb-noservices">—</p>
							)}
						</div>
					</section>

					{/* ── REVIEW ── */}
					{booking.status === "completed" && review && (
						<section className="sf-vb-section">
							<div className="sf-vb-section-head">
								<h2 className="sf-vb-h2">{t.reviewBlock.yourReview}</h2>
							</div>

							<div className="sf-vb-rev-card">
								<div className="sf-vb-rev-head">
									<div className="sf-vb-rev-who">
										<span className="sf-vb-rev-avatar">{review.customerName?.trim()?.charAt(0)?.toUpperCase() || "?"}</span>
										<span>
											<span className="sf-vb-rev-name">{review.customerName || t.reviewBlock.anonymous}</span>
											<span className="sf-vb-rev-date">{dayjs(review.createdAt).locale(language).format("LL")}</span>
										</span>
									</div>
									<span className="sf-vb-stars">{renderStars(review.rating)}</span>
								</div>

								{review.services?.length > 0 && (
									<div className="sf-vb-rev-chips">
										{review.services.map((srv, i) => (
											<span key={i} className="sf-vb-rev-chip">
												{serviceName(srv)}
												{srv.duration ? <em>{t.details.duration.replace("{{count}}", srv.duration)}</em> : null}
											</span>
										))}
									</div>
								)}

								<p className="sf-vb-rev-text">{review.comment || t.reviewBlock.noComment}</p>

								<button
									className="sf-vb-rev-edit"
									onClick={() =>
										navigate(`/reviews/${booking.bookingId}`, {
											state: {
												mode: "edit",
												reviewId: review.reviewId,
												review, // optional, but handy
											},
										})
									}
								>
									{t.reviewBlock.edit}
								</button>
							</div>
						</section>
					)}

					{booking.status === "completed" && !review && (
						<section className="sf-vb-rev-cta">
							<span className="sf-vb-stars sf-vb-stars-cta">{renderStars(0, 26)}</span>
							<h2>{t.reviewBlock.ctaTitle}</h2>
							<p>{t.reviewBlock.ctaText}</p>
							<button onClick={() => navigate(`/reviews/${booking.bookingId}`)}>{t.reviewBlock.ctaBtn}</button>
						</section>
					)}
				</div>

				{/* ═══ SIDEBAR ═══ */}
				<aside className="sf-vb-sidebar">
					{hasPlace && (
						<section className="sf-vb-card">
							{booking.lat && booking.lng && (
								<div className="sf-vb-map">
									<ShopMapPreview lat={booking.lat} lng={booking.lng} />
								</div>
							)}

							<div className="sf-vb-card-body">
								<p className="sf-vb-place">
									<MapPin size={16} strokeWidth={1.7} />
									<span>
										<strong>{booking.address}</strong>
										{(booking.city || booking.country) && <em>{[booking.city, booking.country].filter(Boolean).join(", ")}</em>}
									</span>
								</p>

								<a
									className="sf-vb-directions"
									href={`https://www.google.com/maps/dir/?api=1&destination=${addressQuery}`}
									target="_blank"
									rel="noopener noreferrer"
								>
									<Navigation size={15} strokeWidth={1.9} />
									{t.sidebar.getDirections}
								</a>
							</div>
						</section>
					)}

					{isActive && (
						<section className="sf-vb-card sf-vb-manage">
							<p className="sf-vb-label">{t.manage.title}</p>
							<p className="sf-vb-manage-hint">{t.manage.rescheduleInfo}</p>

							<button className="sf-vb-btn-primary" onClick={handleReschedule}>
								<RefreshCw size={15} strokeWidth={2} />
								{t.manage.rescheduleBtn}
							</button>
							<button className="sf-vb-btn-ghost" onClick={() => setConfirmCancelOpen(true)}>
								<X size={15} strokeWidth={2} />
								{t.manage.cancelBtn}
							</button>
						</section>
					)}
				</aside>
			</div>

			{/* ── Reschedule Modal ── */}
			{isBookingOpen && shopData && rescheduleData && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => {
						setIsBookingOpen(false);
						reloadBooking(); // wait for modal animation to finish
					}}
					availabilityId={shopData.shopId}
					shopData={shopData}
					initialServices={booking.services}
					initialstaffId={booking.staffId}
					mode="reschedule"
					initialPhone={booking.phone || userData?.phone || ""}
					existingBooking={rescheduleData}
				/>
			)}

			{confirmCancelOpen && (
				<div className="sf-vb-confirm-overlay">
					<div className="sf-vb-confirm-modal">
						<span className="sf-vb-confirm-icon">
							<X size={22} strokeWidth={2.2} />
						</span>

						<h3 className="sf-vb-confirm-title">{t.confirm.title}</h3>
						<p className="sf-vb-confirm-text">{t.confirm.text}</p>

						<div className="sf-vb-confirm-actions">
							<button className="sf-vb-confirm-secondary" onClick={() => setConfirmCancelOpen(false)}>
								{t.confirm.back}
							</button>

							<button className="sf-vb-confirm-danger" onClick={confirmCancelBooking}>
								{t.confirm.confirm}
							</button>
						</div>
					</div>
				</div>
			)}

			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</div>
	);
};

export default ViewBookingPage;
